import { Component } from '@angular/core';
import { Validators, FormBuilder, FormGroup } from '@angular/forms';
import { NavController, NavParams, ModalController, ViewController } from 'ionic-angular';
import * as _ from 'lodash';

import { MapPage } from '../map/map';

import { Data } from '../../providers/providers';

@Component({
  selector: 'page-location-edit',
  templateUrl: 'location-edit.html'
})
export class LocationEditPage {

  isReadyToSave: boolean;
  location: any;
  form: FormGroup;

  constructor(public navCtrl: NavController,
              public modalCtrl: ModalController,
              public viewCtrl: ViewController,
              public navParams: NavParams,
              formBuilder: FormBuilder,
              public data: Data)
  {
    this.location = this.navParams.get('location');

    if (!this.location) {
      this.location = {
        id: "",
        year: new Date().getFullYear(),
        farm: "",
        block: "",
        variety: "",
        weather_station: { id: null, title: null },
        green_tip_date: null,
        bloom_date: null,
        pct_spurs_flowering: null,
        orchard_age: "",
        spacing_in_row: 0,
        spacing_between_rows: 0,
        trees_per_acre: 0,
        tree_width: 0,
        tree_height: 0,
        tree_row_volume: 0,
        emitter_spacing_in_row: 0,
        emitter_spacing_between_rows: 0,
        emitters_per_acre: 0,
        emitter_flow: 0,
        application_rate: 0,
        water_holding_capacity: 0
      };
    }

    // NB: weather_station attribute is edited using custom modal, so we don't include it in the normal form...
    this.form = formBuilder.group({
      id: [this.location.id, null], // not shown in view
      owner: [data.userKey, Validators.required], // not shown in view
      year: [this.location['year'], Validators.required],
      farm: [this.location['farm'], Validators.required],
      block: [this.location['block']],
      variety: [this.location['variety'], Validators.required],
      weather_station_title_dummy: [null],
      green_tip_date: [this.location['green_tip_date']],
      bloom_date: [this.location['bloom_date']],
      pct_spurs_flowering: [this.location['pct_spurs_flowering']],
      orchard_age: [this.location['orchard_age']],
      spacing_in_row: [this.location['spacing_in_row']],
      spacing_between_rows: [this.location['spacing_between_rows']],
      trees_per_acre: [this.location['trees_per_acre']],
      tree_width: [this.location['tree_width']],
      tree_height: [this.location['tree_height']],
      tree_row_volume: [this.location['tree_row_volume']],
      emitter_spacing_in_row: [this.location['emitter_spacing_in_row']],
      emitter_spacing_between_rows: [this.location['emitter_spacing_between_rows']],
      emitters_per_acre: [this.location['emitters_per_acre']],
      emitter_flow: [this.location['emitter_flow']],
      application_rate: [this.location['application_rate']],
      water_holding_capacity: [this.location['water_holding_capacity']],
    });

    this.updateReadyToSave();
    // Watch the form for changes, and
    this.form.valueChanges.subscribe((v) => {
      this.updateReadyToSave();
    });
  }

  requiredForThinning(field) {
    return [
      'year',
      'farm',
      'variety',
    ].indexOf(field) != -1;
  }

  requiredForIrrigation(field) {
    return [
      'year',
      'farm',
      'variety',
      'weather_station_title_dummy',
      'green_tip_date',
      'orchard_age',
      'application_rate',
      'water_holding_capacity',
      'spacing_in_row',
      'spacing_between_rows',
      'trees_per_acre',
      'emitter_spacing_in_row',
      'emitter_spacing_between_rows',
      'emitters_per_acre',
      'emitter_flow',
      ].indexOf(field) != -1;
  }

  requiredForCarbohydrate(field) {
    return [
      'year',
      'farm',
      'variety',
      'weather_station_title_dummy',
      'green_tip_date',
      'bloom_date',
      'pct_spurs_flowering',
    ].indexOf(field) != -1;
  }

  updateReadyToSave() {
    this.isReadyToSave = this.form.valid;
  }

  // update trees_per_acre and emitters_per_acre values based on other inputs
  inputChanged(field) {

    const self = this;

    // IMPORTANT: must set these fields to null if they become empty from an edit on the form -
    // otherwise an empty value can trigger an error in location-detail:
    //     InvalidPipeArgument: '' for pipe 'DecimalPipe'

    if (field == 'spacing_in_row' ||
        field == 'spacing_between_rows' ||
        field == 'tree_width' ||
        field == 'tree_height' ||
        field == 'tree_row_volume' ||
        field == 'emitter_spacing_in_row' ||
        field == 'emitter_spacing_between_rows' ||
        field == 'emitters_per_acre' ||
        field == 'emitter_flow')
    {
      if (!self.location[field]) {
        self.location[field] = null;
      }
    }

    if (field == 'tree_width' || field == 'tree_height') {
      let trv = Math.round(
        this.location['tree_width'] *
            this.location['tree_height'] *
            (43560 / this.location['spacing_between_rows']) *
            (0.7/1000)
      );
      if (trv == Infinity || trv == undefined || _.isNaN(trv)) {
        trv = null;
      }
      this.location['tree_row_volume'] = trv;
    }

    if (field == 'spacing_in_row' || field == 'spacing_between_rows') {
      let tpa = Math.round(
        43560 / (this.location['spacing_in_row'] * this.location['spacing_between_rows'])
      );
      if (tpa == Infinity) {
        tpa = null;
      }
      this.location['trees_per_acre'] = tpa;
    }

    if (field == 'emitter_spacing_in_row' || field == 'emitter_spacing_between_rows') {
      let epa = Math.round(
        43560 / (this.location['emitter_spacing_in_row'] * this.location['emitter_spacing_between_rows'])
      );
      if (epa == Infinity) {
        epa = null;
      }
      this.location['emitters_per_acre'] = epa;

      let ar = Math.round(
        this.location['emitters_per_acre'] * this.location['emitter_flow']
      );
      if (ar == Infinity) {
        ar = null;
      }
      this.location['application_rate'] = ar;
    }

    if (field == 'emitter_flow') {
      let ar = Math.round(
        this.location['emitters_per_acre'] * this.location['emitter_flow']
      );
      if (ar == Infinity) {
        ar = null;
      }
      this.location['application_rate'] = ar;
    }
  }

  selectWeatherStation() {
    let mapModal = this.modalCtrl.create(MapPage, {weather_station: this.location['weather_station']});

    mapModal.onDidDismiss(weather_station => {
      if (weather_station) {
        this.location['weather_station'] = weather_station;
      }
      else {
        this.location['weather_station'] = null; // make sure weather_station is not undefined
      }
    });
    mapModal.present();
  }

  /**
   * The user cancelled, so we dismiss without sending data back.
   */
  cancel() {
    this.viewCtrl.dismiss();
  }

  /**
   * The user is done and wants to create the item, so return it
   * back to the presenter.
   */
  done() {
    if (!this.form.valid) { return; }
    const updatedLocation = this.form.value;
    updatedLocation['weather_station'] = this.location['weather_station'] ? this.location['weather_station'] : null;
    this.viewCtrl.dismiss(updatedLocation);
  }
}



// WEBPACK FOOTER //
// ./src/pages/location-edit/location-edit.ts